/** @description 流程图配置列表页面 */
import { Space, Table, Button, Form, Input, Modal, message, Switch, Popconfirm } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useState, useReducer, useMemo } from 'react';
import { history, useAccess, Access } from 'umi';

import { request } from '@/utils/request';
import moment from 'moment';
import MainPage from '@/components/page-main';
import fetchValueReducer from '@/hooks/fetchValueReducer';
import useFetch from '@/hooks/useFetch';
import { SOURCE_CATEGORY } from '@bb8/config';
import useStaff from '@/hooks/useStaff';

export enum WORKFLOW_STATUS {
  正常 = '待生效',
  待生效 = '待生效',
  已生效 = '已生效',
}

export const TimeComponent = (time: Date) => (
  <div>{moment(time).format('YYYY-MM-DD HH:mm:ss')}</div>
);

export const buttonContainerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '10px 0',
  borderTop: '1px solid rgba(0, 0, 0, 0.1)',
  marginTop: '10px',
} as React.CSSProperties;

const TaskIndex: React.FC = () => {
  const [form] = Form.useForm();
  const [fetchValue, dispatchFetchValue] = useReducer(fetchValueReducer, { page: 1, size: 10, total: 1 });
  const { data, loading, fetchData } = useFetch('/local/workflow', { ...fetchValue, ...form.getFieldsValue() }, dispatchFetchValue);

  const { data: staffList } = useStaff();

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [deleteFlow, setDeleteFlow] = useState<any>({});
  const [deleteText, setDeleteText] = useState<string>('');
  const [workflowName, setWorkflowName] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [copyWorkflowId, setCopyWorkflowId] = useState<number>(0);

  const access = useAccess();

  const [permitVisible, setPermitVisible] = useState<boolean>(false);
  const [contentId, setContentId] = useState<number>(0);
  const [userKeyword, setUserKeyword] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<string[]>([]);

  useEffect(() => {
    if (!isModalOpen) {
      setWorkflowName('');
      setCopyWorkflowId(0);
    }
  }, [isModalOpen])

  const currentStaff = useMemo(() => {
    if (!userKeyword) return staffList;
    return staffList.filter((item: { name: string; userid: string }) => item.name.includes(userKeyword));
  }, [userKeyword, staffList])

  const columns: ColumnsType<API.Workflow> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '标题',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <div>{name ? name : '-'}</div>,
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version',
    },
    // {
    //   title: '是否激活',
    //   dataIndex: 'status',
    //   render: (_, record) => <Popconfirm title="确定激活该流程？" onConfirm={() => handleActivation(record)}>
    //     <Switch checkedChildren="是" unCheckedChildren="否" checked={_ === '激活中'} />
    //   </Popconfirm>
    // },
    {
      title: '创建者',
      dataIndex: 'creator',
      render: _ => _.name + '-' + _.userid,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_) => TimeComponent(_),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_) => TimeComponent(_),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Access accessible={_.setMaker}>
            <a onClick={() => handlePermit(record?.id)}>设置协作者</a>
          </Access>
          <Access accessible={access.workflowEdit && _.permit} fallback={<a onClick={() => history.push(`/workflow/${_.id}`)}>预览</a>}>
            <a onClick={() => history.push(`/workflow/${_.id}`)}>编辑</a>
          </Access>
          <Access accessible={access.workflowCopy}>
            <a onClick={() => handleCopy(_.id)}>复制</a>
          </Access>
          <Access accessible={access.administrator || _.setMaker}>
            <a onClick={() => handleModal(_)}>删除</a>
          </Access>
        </Space>
      ),
    },
  ];

  const handlePermit = (contentId: number) => {
    setPermitVisible(true);
    setContentId(contentId);
  }

  const handleSetPermit = async () => {
    if (!contentId || selectedUser.length === 0) return message.warning("请先选择协作者！");
    const { resp } = await request('/local/editor', {
      method: "POST",
      data: {
        users: selectedUser.map(vo => {
          return staffList.find((item: { userid: string; name: string }) => item.userid === vo)
        }),
        contentId,
        contentCategory: SOURCE_CATEGORY.WORKFLOW,
      }
    })
    if (resp?.code !== 0) return message.error(resp?.message);
    message.success(resp?.message);
    clearPermit();
  }

  const clearPermit = () => {
    setPermitVisible(false);
    setContentId(0);
    setSelectedUser([]);
    setUserKeyword("");
  }

  const handleCopy = async (id: number) => {
    setCopyWorkflowId(id);
    setIsModalOpen(true);
  }

  const copyWorkflow = async () => {
    if (!workflowName) return message.warning('流程标题不能为空！');
    if (!copyWorkflowId) return;
    const { resp, error } = await request(`/local/workflow/copy`, {
      method: 'post',
      data: { workflow_id: copyWorkflowId, name: workflowName }
    });
    if (resp?.code !== 0) return message.error(error?.message);
    fetchData();
    message.success(resp.message);
    setIsModalOpen(false);
  }

  /** @description 激活 */
  const handleActivation = async (data: any) => {
    if (data.status === '激活中') {
      message.warning('该流程已经激活！')
      return true;
    }
    const { resp, error } = await request(`/local/workflow/activate/${data?.id}`, {
      method: 'post',
    });
    if (resp?.code !== 0) {
      message.error(error?.message);
      return true;
    }
    message.success(resp.message);
    fetchData();
  }

  /** @description 展示删除弹窗 */
  const handleModal = (data: {}) => {
    setDeleteFlow(data);
    setIsVisible(true);
  };

  /** @description 确认删除 */
  const handleOk = async () => {
    if (deleteText !== '确认删除' || !deleteFlow.id) return;
    const { resp } = await request(`/local/workflow/${deleteFlow.id}`, {
      method: 'delete',
    });
    if (resp?.code !== 0) return message.error('删除失败！');
    message.success(resp.message);
    handleClear();
    fetchData();
  };

  /** @description 取消删除 */
  const handleCancel = () => handleClear();

  /** @description 重置数据 */
  const handleClear = () => {
    setIsVisible(false);
    setDeleteText('');
  };

  return (
    <>
      <MainPage>
        <Form
          form={form}
          initialValues={{ version: '', status: null, name: '' }}
          layout="inline"
          onValuesChange={() => {
            dispatchFetchValue({ type: 'update', value: { page: 1 } });
            fetchData();
          }}
        >
          <Form.Item label="标题" name="name">
            <Input placeholder="请输入标题" allowClear />
          </Form.Item>
          <Form.Item label="版本" name="version">
            <Input placeholder="请输入版本" allowClear />
          </Form.Item>
        </Form>
        <Access accessible={access.workflowCreate}>
          <div style={buttonContainerStyle}>
            <Button type="primary" onClick={() => history.push('/workflow/:id')}>
              新建流程
            </Button>
          </div>
        </Access>
        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            current: fetchValue.page,
            total: fetchValue.total,
            pageSize: fetchValue.size,
            pageSizeOptions: [10, 20, 50],
            onChange: (page, size) => {
              dispatchFetchValue({ type: 'update', value: { page, size } });
              fetchData();
            },
          }}
        />
      </MainPage>

      <Modal
        title={'删除版本' + deleteFlow?.version ?? ''}
        visible={isVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
      >
        <Space style={{ marginBottom: 10 }}>
          输入“<span style={{ color: 'red' }}>确认删除</span>
          ”并点击确定即可删除该版本
        </Space>
        <Input
          placeholder="请完整输入确认删除"
          value={deleteText}
          onChange={(e) => setDeleteText(e.target.value)}
        />
      </Modal>

      <Modal title="你确定复制该流程吗？" width={400} visible={isModalOpen} destroyOnClose onOk={copyWorkflow} onCancel={() => setIsModalOpen(false)}>
        <Input placeholder='请输入流程标题' value={workflowName} onChange={(e) => setWorkflowName(e.target.value)} />
      </Modal>

      <Modal 
        width={'80vw'} 
        title="请选择协作者并确定" 
        visible={permitVisible} 
        onCancel={clearPermit} 
        onOk={handleSetPermit}
      >
        <Space direction="vertical">
          <Input placeholder="输入关键字" onChange={e => setUserKeyword(e.target.value)} />
          <div style={{width: '100%'}}>
            {
              currentStaff.map((s: { name: string; userid: string }) => <Button style={{ marginBottom: 10, marginRight: 10 }} key={s.userid} type={selectedUser.includes(s.userid) ? 'primary' : 'default'} onClick={() => {
                if (selectedUser.includes(s.userid)) {
                  setSelectedUser(selectedUser.filter((userid) => userid !== s.userid));
                } else {
                  setSelectedUser([...selectedUser, s.userid]);
                }
              }}>
                {s.name}-{s.userid}
              </Button>)
            }
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default TaskIndex;
